<template>
  <div class="ordering-wrapper">
    <label for="ordering-order-by" class="ordering-label">{{ title }}</label>

    <div class="ordering-content">
      <div class="row ordering-controls-inline">
        <select
          id="ordering-order-by"
          :value="effectiveOrderBy"
          :disabled="saving"
          @change="handleOrderByChange"
        >
          <option value="created_at">{{ s__('Boards|Created At') }}</option>
          <option value="title">{{ s__('Boards|Title') }}</option>
          <option value="priority">{{ s__('Boards|Priority') }}</option>
          <option value="due_at">{{ s__('Boards|Due Date') }}</option>
          <option value="updated_at">{{ s__('Boards|Updated At') }}</option>
        </select>
        <button
          type="button"
          class="direction-toggle-btn"
          :class="{ 'is-desc': effectiveOrderDirection === 'desc' }"
          :title="
            effectiveOrderDirection === 'desc' ? s__('Boards|Descending') : s__('Boards|Ascending')
          "
          :aria-label="
            effectiveOrderDirection === 'desc' ? s__('Boards|Descending') : s__('Boards|Ascending')
          "
          :disabled="saving"
          @click="toggleDirection"
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M21.1445 19.793C21.1445 20.4492 21.5547 20.8359 22.2227 20.8359H24.9297C25.6094 20.8359 26.0312 20.4492 26.0312 19.793V4.04297C26.0312 3.38672 25.6094 3 24.9297 3H22.2227C21.5547 3 21.1445 3.38672 21.1445 4.04297V19.793Z"
              fill="currentColor"
              fill-opacity="0.85"
            />
            <path
              d="M14.793 19.793C14.793 20.4492 15.2148 20.8359 15.8828 20.8359H18.5898C19.2695 20.8359 19.6914 20.4492 19.6914 19.793V7.39453C19.6914 6.73828 19.2695 6.35156 18.5898 6.35156H15.8828C15.2148 6.35156 14.793 6.73828 14.793 7.39453V19.793Z"
              fill="currentColor"
              fill-opacity="0.85"
            />
            <path
              d="M8.45312 19.793C8.45312 20.4492 8.86328 20.8359 9.53125 20.8359H12.25C12.918 20.8359 13.3398 20.4492 13.3398 19.793V10.7461C13.3398 10.1133 12.918 9.71484 12.25 9.71484H9.53125C8.86328 9.71484 8.45312 10.1133 8.45312 10.7461V19.793Z"
              fill="currentColor"
              fill-opacity="0.85"
            />
            <path
              d="M2.10156 19.793C2.10156 20.4492 2.52344 20.8359 3.20312 20.8359H5.91016C6.57812 20.8359 7 20.4492 7 19.793V14.1094C7 13.4531 6.57812 13.0547 5.91016 13.0547H3.20312C2.52344 13.0547 2.10156 13.4531 2.10156 14.1094V19.793Z"
              fill="currentColor"
              fill-opacity="0.85"
            />
          </svg>
        </button>
      </div>
    </div>

    <div v-if="showSaveAsDefault && canSaveAsDefault" class="ordering-actions">
      <button
        type="button"
        class="btn-secondary btn-sm"
        :disabled="saving"
        @click="handleSaveAsDefault"
      >
        <span v-if="saving">{{ s__('Boards|Saving...') }}</span>
        <span v-else>{{ saveAsDefaultLabel }}</span>
      </button>
    </div>

    <div v-if="error" class="error">{{ error }}</div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { s__ } from '@/locale'

const props = defineProps({
  orderBy: { type: String, default: '' },
  orderDirection: { type: String, default: '' },
  defaultOrderBy: { type: String, default: 'created_at' },
  defaultOrderDirection: { type: String, default: 'desc' },
  title: { type: String, default: () => s__('Boards|Ordering') },
  resetLabel: { type: String, default: () => s__('Boards|Reset Session Ordering') },
  saveAsDefaultLabel: { type: String, default: () => s__('Boards|Save as Board Default') },
  showReset: { type: Boolean, default: true },
  showSaveAsDefault: { type: Boolean, default: false },
  canSaveAsDefault: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  error: { type: String, default: '' },
})

const emit = defineEmits([
  'update:orderBy',
  'update:orderDirection',
  'change',
  'reset',
  'saveAsDefault',
])

const effectiveOrderBy = computed(() => props.orderBy || props.defaultOrderBy || 'created_at')
const effectiveOrderDirection = computed(
  () => props.orderDirection || props.defaultOrderDirection || 'desc'
)

function emitOrderingChange(orderBy, orderDirection) {
  emit('update:orderBy', orderBy)
  emit('update:orderDirection', orderDirection)
  emit('change', { orderBy, orderDirection })
}

function handleOrderByChange(event) {
  const nextOrderBy = event.target.value || 'created_at'
  emitOrderingChange(nextOrderBy, effectiveOrderDirection.value)
}

function toggleDirection() {
  const nextDirection = effectiveOrderDirection.value === 'desc' ? 'asc' : 'desc'
  emitOrderingChange(effectiveOrderBy.value, nextDirection)
}

function handleSaveAsDefault() {
  emit('saveAsDefault', {
    orderBy: effectiveOrderBy.value,
    orderDirection: effectiveOrderDirection.value,
  })
}
</script>

<style scoped>
.ordering-wrapper {
  margin-bottom: 16px;
}

.ordering-label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
}

.ordering-controls-inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

.row.ordering-controls-inline {
  margin: 0;
}

.ordering-controls-inline select {
  flex: 1;
  min-width: 0;
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
}

.direction-toggle-btn {
  width: 54px;
  height: 36px;
  padding: 12px 6px;
  box-sizing: border-box;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background: #fff;
  color: #333;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.direction-toggle-btn svg {
  width: 18px !important;
  height: 18px;
  display: block;
  color: #333;
}

.direction-toggle-btn:hover:not(:disabled) {
  background: #f8f9fa;
}

.direction-toggle-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.direction-toggle-btn.is-desc svg {
  transform: scaleX(-1);
}

.ordering-actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.error {
  color: #dc3545;
  font-size: 13px;
  margin-top: 8px;
}
</style>
